import React, { useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { setFilter as setFilterAction, getSearchedTickets, resetFilter } from "../../../../actions/projectmail";
import { initialSearchFilter } from "../../../../reducers/projectmail";
import { useDefaultPasscodeStyles } from "../../../../constants/passCodeUtils";

const type = "closed";

const CloseFilterComponent = forwardRef((props, ref) => {
  const {
    filter,
    setFilterFn,
    requestFn,
    resetFilterFn,
    openFilter,
    translate,
    resetFn,
    inProgress,
    setInProgress,
    closeFilter,
  } = props;
  const pc = useDefaultPasscodeStyles();

  const filterRef = useRef(filter);
  const openFilterRef = useRef(openFilter);
  const inProgressRef = useRef(inProgress);
  const requestFnRef = useRef(requestFn);
  const setInProgressRef = useRef(setInProgress);
  useImperativeHandle(ref, () => ({
    setFilter: (filterValue, filterKey) => {
      setFilterFn({ ...filter, [filterKey]: filterValue, filterUsed: true });
    },
  }));

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    openFilterRef.current = openFilter;
  }, [openFilter]);

  useEffect(() => {
    inProgressRef.current = inProgress;
  }, [inProgress]);

  useEffect(() => {
    requestFnRef.current = requestFn;
  }, [requestFn]);

  useEffect(() => {
    setInProgressRef.current = setInProgress;
  }, [setInProgress]);

  useEffect(() => {
    const enterEvent = async e => {
      if (e.key === "Enter" && openFilterRef.current === true && !inProgressRef.current) {
        setInProgressRef.current(true);
        await requestFnRef.current(filterRef.current, type);
        setInProgressRef.current(false);
      }
    };

    window.addEventListener("keydown", enterEvent);
    return () => {
      window.removeEventListener("keydown", enterEvent);
    };
  }, []);

  useEffect(() => {
    if (filter === undefined) resetFilterFn(type);
  }, [filter, resetFilterFn]);

  const handleTextChange = name => event => {
    setFilterFn({ ...filter, [name]: event.target.value, filterUsed: true });
  };

  const textfield = (label, key) => (
    <TextField
      label={label}
      sx={[pc.textField, pc.lineExpander]}
      value={filter ? filter[key] : ""}
      onChange={handleTextChange(key)}
    />
  );

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
      {textfield(translate.title, "title")}
      {/** TODO: change id to externalId */}
      {textfield(translate.externalId, "externalId")}
      <Box sx={pc.lineBreak} />
      {textfield(translate.customer, "customer")}
      {textfield(translate.EmailTicketSystemOwner, "owner")}
      <Box sx={pc.lineBreak} />
      <Box sx={pc.lineBreak} />
      {textfield(translate.orderID, "orderID")}
      <Divider />
      <Box sx={{ mt: 1, width: "100%", display: "inline-flex" }}>
        <Box sx={{ flex: 1, width: "10px" }} />
        <Button
          size="small"
          onClick={async () => {
            resetFn();
            resetFilterFn(type);
            setInProgress(true);
            await requestFn(initialSearchFilter, type);
            closeFilter();
            setInProgress(false);
          }}
          disabled={inProgress}
        >
          Reset
        </Button>
        <Button
          disabled={inProgress}
          variant="contained"
          size="small"
          color="primary"
          onClick={async () => {
            setInProgress(true);
            await requestFn(filter, type);
            closeFilter();
            setInProgress(false);
          }}
        >
          Suche
        </Button>
      </Box>
    </Box>
  );
});

CloseFilterComponent.defaultProps = {
  filter: undefined,
};

CloseFilterComponent.propTypes = {
  filter: PropTypes.object,
  setFilterFn: PropTypes.func.isRequired,
  requestFn: PropTypes.func.isRequired,
  resetFilterFn: PropTypes.func.isRequired,
  openFilter: PropTypes.bool.isRequired,
  translate: PropTypes.object.isRequired,
  resetFn: PropTypes.func.isRequired,
  inProgress: PropTypes.bool.isRequired,
  setInProgress: PropTypes.func.isRequired,
  closeFilter: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filter: state.projectmail.searchFilter[type],
  translate: state.ui.translate,
});

const mapDispatchToProps = {
  setFilterFn: filter => dispatch => dispatch(setFilterAction(filter, type)),
  requestFn: filter => async dispatch => {
    await dispatch(getSearchedTickets(filter, type));
  },
  resetFilterFn: () => dispatch => dispatch(resetFilter(type)),
};

const CloseFilter = connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
)(CloseFilterComponent);

export default CloseFilter;
